import {
  HttpContext,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from "@angular/common/http";
import { Injector, inject, runInInjectionContext } from "@angular/core";
import { environment } from "../../../environments/environment";
import {
  Observable,
  catchError,
  from,
  map,
  switchMap,
  throwError,
} from "rxjs";
import { Router } from "@angular/router";
import { MatSnackBarService } from "../services/snackbar-service/snackbar.service";
import { AuthDavesaV2Service } from "./auth-davesa-v2.service";
import { AUTH_DAVESA_API, CSRF_RETRIED } from "./auth-davesa-api.component";
import { ErrorConstants } from "../util/app-constants";
import { TokenType } from "../enum/token-type.enum";
import { SecureTokenStorageService } from "../services/secure-token-storage.service";
import { LogoutReason } from "../constants/inactivity.constants";
import { UNSAFE_HTTP_METHODS } from "../constants/http.constants";

// A 401 from one of these must never trigger a refresh-and-retry - that's
// either the auth endpoint reporting its own failure (wrong password,
// invalid 2FA code, refresh itself rejected) or a place the loop would
// otherwise retry forever.
const AUTH_ENDPOINT_PATTERNS = [
  '/auth/refresh',
  '/auth/login',
  '/auth/select-role',
  '/auth/logout',
  // v1 org console calls are relative ("auth/org/refresh"), hence no leading slash.
  'auth/org/',
  'auth/org-signin',
  '/2fa/verify',
  '/2fa/setup',
  '/2fa/verify-password',
  '/2fa/disable',
];

/**
 * Interceptor
 *
 * @param req
 * @param next
 */
export const authDavesaApiInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  if (!req.context.get(AUTH_DAVESA_API)) {
    return next(req);
  }

  // After a reload the CSRF token is gone from memory. GETs don't need it, so
  // only a state-changing request waits (once, shared) for it to be re-fetched.
  const injector = inject(Injector);
  const auth = inject(AuthDavesaV2Service);
  const needsCsrfToken = UNSAFE_HTTP_METHODS.includes(req.method) && auth.isLoggedIn() && !auth.csrfToken();
  if (needsCsrfToken) {
    return from(auth.ensureCsrfToken()).pipe(
      switchMap(() => runInInjectionContext(injector, () => sendAuthenticated(req, next)))
    );
  }
  return sendAuthenticated(req, next);
};

const markCsrfRetried = (req: HttpRequest<unknown>): HttpRequest<unknown> => {
  const context = new HttpContext();
  for (const token of req.context.keys()) {
    context.set(token, req.context.get(token));
  }
  context.set(CSRF_RETRIED, true);
  return req.clone({ context });
};

const sendAuthenticated = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  if (req.context.get(AUTH_DAVESA_API)) {
    const _router = inject(Router);
    const _matSnackBarService = inject(MatSnackBarService);
    const _authDavesaV2Service = inject(AuthDavesaV2Service);
    const _secureTokenStorage = inject(SecureTokenStorageService);
    const _injector = inject(Injector);

    _authDavesaV2Service.initiate();

    const domain = _authDavesaV2Service.orgDomain();
    const deviceFingerprint = _secureTokenStorage.getDeviceFingerprint();

    // Check if the request is for v2 API
    const isV2Api = req.url.startsWith('/api/v2') || req.url.startsWith('api/v2');

    // req.url, not urlWithParams: the clone keeps req.params and HttpClient appends them when sending,
    // so building from urlWithParams sent every `params:` value twice (an array on the API side)
    let url;
    if (isV2Api) {
      // For v2 API, construct URL without /api/v1/ prefix
      const baseUrl = environment.API_URL.replace('/api/v1/', '/').replace(/\/$/, ''); // Remove trailing slash
      const cleanPath = req.url.startsWith('/') ? req.url : '/' + req.url;
      url = baseUrl + cleanPath;
    } else {
      // For v1 API, use standard baseUrl
      url = environment.API_URL + req.url;
    }

    let authReq;
    // Build headers object - the session itself rides on the dvs_* cookie
    // (withCredentials below), there's no token to attach here anymore.
    const headers: any = {};

    // Add domain if available
    if (domain && domain.trim()) {
      headers.domain = domain;
    }

    // Add device fingerprint for security tracking
    if (deviceFingerprint) {
      headers["X-Device-Fingerprint"] = deviceFingerprint;
    }

    // Attach the CSRF token on state-changing requests once the backend
    // has issued one.
    const csrfToken = _authDavesaV2Service.csrfToken();
    if (csrfToken && UNSAFE_HTTP_METHODS.includes(req.method)) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    // Clone request with headers
    authReq = req.clone({
      url: url,
      setHeaders: headers,
      withCredentials: true,
    });

    const sentAt = Date.now();

    return next(authReq).pipe(
        map((event: HttpEvent<unknown>) => {
          return event;
        }),
        catchError((error) => {
          if (error) {
            // _matSnackBarService.warning(`${error.status}: ${error.error.error}`);
            switch (error.status) {
              case 400:
                if (error.error?.error === ErrorConstants.UNAUTHORIZED_PERMISSION || error.error?.error === ErrorConstants.ACCESS_DENIED) {
                  _matSnackBarService.warning(ErrorConstants.UNAUTHORIZED_PERMISSION);
                }
                break;
              case 401: {
                const isHeartbeat = req.url.includes('/heartbeat');

                // A 401 from the auth flow itself (wrong password, invalid
                // 2FA code, refresh rejected, ...) must never trigger a
                // refresh-and-retry - let the calling component handle it.
                const isAuthEndpoint = AUTH_ENDPOINT_PATTERNS.some((p) => req.url.includes(p));
                if (isAuthEndpoint) {
                  return throwError(() => error);
                }

                // A pending-2FA session can't be refreshed into a full one -
                // it has to go through /2fa/verify.
                if (_authDavesaV2Service.decodedToken()?.tokenType === TokenType.TEMP_2FA) {
                  _authDavesaV2Service.removeToken();
                  _router.navigateByUrl("/sign-in");
                  return throwError(() => error);
                }

                // Backend-declared inactivity is a hard stop - refreshing
                // wouldn't help, the session was deliberately ended.
                if (error?.error?.reason === LogoutReason.INACTIVITY) {
                  _authDavesaV2Service.signOutDueToInactivity();
                  _router.navigateByUrl(`/sign-in?reason=${LogoutReason.INACTIVITY}`);
                  return throwError(() => error);
                }
                if (error?.error?.accountLocked) {
                  _authDavesaV2Service.removeTokenWithReason('account_locked');
                  _router.navigateByUrl(`/sign-in?reason=${LogoutReason.ACCOUNT_LOCKED}`);
                  return throwError(() => error);
                }

                // If backend signals QuickBooks re-auth is required, let the component handle it
                if (error?.error?.requiresReauth) {
                  return throwError(() => error);
                }

                // The request went out before a refresh that has since
                // completed, so it used the old cookie: just resend it.
                if (_authDavesaV2Service.getLastRefreshAt() > sentAt) {
                  return next(authReq);
                }

                // Otherwise treat it as an expired access cookie: one
                // silent refresh (shared with any concurrent 401s), then
                // resend unchanged - the new cookie is already set. Only a
                // failed refresh signs out; an error from the resend is the
                // request's own error and is returned as-is.
                return from(_authDavesaV2Service.refreshAuthToken()).pipe(
                  catchError((refreshError) => {
                    const reason = isHeartbeat ? LogoutReason.UNAUTHORIZED : undefined;
                    if (isHeartbeat) {
                      _authDavesaV2Service.removeTokenWithReason('unauthorized');
                    } else {
                      _authDavesaV2Service.removeToken();
                    }
                    _router.navigateByUrl(reason ? `/sign-in?reason=${reason}` : "/sign-in");
                    return throwError(() => refreshError);
                  }),
                  switchMap(() => next(authReq)),
                );
              }
              case 403: {
                // The token is bound to the session: after another tab signs in again, or the API
                // rotated its keys, the one held here no longer matches. Fetch the current one and
                // send the request once more; a second rejection is reported as usual.
                if (
                  error.error?.csrfInvalid === true &&
                  !req.context.get(CSRF_RETRIED) &&
                  UNSAFE_HTTP_METHODS.includes(req.method) &&
                  _authDavesaV2Service.isLoggedIn()
                ) {
                  _authDavesaV2Service.clearCsrfToken();
                  return from(_authDavesaV2Service.ensureCsrfToken()).pipe(
                    switchMap(() =>
                      runInInjectionContext(_injector, () => sendAuthenticated(markCsrfRetried(req), next))
                    )
                  );
                }
                console.log("error", error);
                if (typeof error.error?.message === 'string' && error.error.message.startsWith("Access denied")) {
                  _matSnackBarService.warning(ErrorConstants.UNAUTHORIZED_PERMISSION);
                }
                break;
              }
              case 404:
                console.log("error", error);
                break;
              case 500:
                console.log("error", error);
                break;
              case 299:
                console.log("error", error);
              default:
                console.log("error", error);
                break;
            }
          }
          return throwError(() => error);
        })
      );
  }
  return next(req);
};
